<template>
  <form @submit.prevent="submit">
    <naj-box :title="local.lawsuitDetails.caseCategoryTitle">
      <template
        v-if="caseData.isSmartCaseType != null &&isNewMode"
        v-slot:header
      >
        <div style="position: absolute;left: 1rem;top: 0.95rem;">
          <a
            :class="{ disabled: mode.isViewMode || auditFields.isCaseTypeAudited }"
            @click="switchRequest"
          >
            <span v-if="caseData.isSmartCaseType">
              {{ local.caseTypeTree.caseTreeBtnOld }}
            </span>
            <span v-else>
              {{ local.caseTypeTree.caseTreeBtn }}
            </span>
            <v-icon
              style="margin-right: 10px"
              right
              class="tab-button-icon"
            >
              mdi-arrow-left
            </v-icon>
          </a>
        </div>
      </template>

      <div v-if="caseData.isSmartCaseType == null && isNewMode">
        <v-row
          justify="center"
          align="center"
          class="my-6"
        >
          <v-col
            cols="12"
            class="text-center"
          >
            <span class="text-h6 font-weight-medium">
              {{ local.lawsuitDetails.smartOptionTxt }}
            </span>
          </v-col>

          <v-col
            cols="12"
            class="text-center"
          >
            <naj-button
              color="primary"
              class="mx-2"
              @click="onSelect(true)"
            >
              {{ local.common.yes }}
            </naj-button>

            <naj-button
              color="secondary"
              class="mx-2"
              @click="onSelect(false)"
            >
              {{ local.lawsuitDetails.noHelp }}
            </naj-button>
          </v-col>
        </v-row>
      </div>

      <template v-else>
        <v-row v-if="caseData.isSmartCaseType && isNewMode">
          <v-col cols="12">
            <naj-alert type="information">
              <template slot="content">
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <strong class="blue--text"> {{ local.lawsuitDetails.smartOptionNoteHead }} </strong>
                    <div class="mt-1 text--secondary">
                      {{ local.lawsuitDetails.smartOptionNoteContent }}
                    </div>
                  </div>
                </div>
              </template>
            </naj-alert>
          </v-col>
          <v-col
            cols="12"
            lg="12"
            style="padding:10px"
          >
            <naj-tree
              id="caseTypeId"
              :node-result-value="'caseTypeId'"
              :items-list="questions"
              :loading="loading"
              :root-item="rootItem"
              :preselected-answers="caseData.caseTypeQuestionList"
              :error-message="errors.first('caseTypeId')"
              :disabled="mode.isViewMode || auditFields.isCaseTypeAudited"
              @all-selections="handleAnswer"
              @final-result="handleFinalRes"
            ></naj-tree>
            <template v-if="showStepCaseCategoryDetailsPanel && caseData.caseTypeId != null">
              <v-card
                class="pa-4 text-right"
                outlined
                elevation="1"
                max-width="300"
              >
                <!-- Top Icons -->
                <div class="d-flex justify-space-between align-center mb-2">
                  <v-icon color="primary">
                    mdi-account
                  </v-icon>
                  <v-icon color="amber darken-2">
                    mdi-star-outline
                  </v-icon>
                </div>

                <!-- Title -->
                <h3 class="text-subtitle-1 font-weight-bold green--text mb-2">
                  {{ selectedCaseType.name }}
                </h3>

                <!-- Description -->
                <p class="text-body-2 mb-4">
                  {{ selectedCaseType.notes }}
                </p>

                <!-- Tag + Icon Row -->
                <div class="d-flex justify-start align-center mb-4">
                  <v-chip
                    small
                    color="grey lighten-3"
                    text-color="black"
                    class="ml-2"
                  >
                    {{ selectedClassification.name }}
                  </v-chip>
                  <v-icon small>
                    mdi-calendar
                  </v-icon>
                </div>

                <!-- Action Button -->
                <div>
                  <naj-button
                    v-if="!selectedCaseType.isRedirectToExternalService"
                    type="submit"
                  >
                    <v-icon
                      left
                      small
                    >
                      mdi-arrow-left
                    </v-icon>
                    {{ local.lawsuitDetails.selectCaseType }}
                  </naj-button>
                  <naj-button
                    v-else
                    @click="redirectExternalServiceUrl"
                  >
                    <v-icon
                      left
                      small
                    >
                      mdi-arrow-left
                    </v-icon>
                    {{ local.lawsuitDetails.selectCaseType }}
                  </naj-button>
                </div>
              </v-card>
            </template>

            <v-col
              v-if="isEditMode && !isInternalLawsuit && isNotCompleted"
              cols="12"
              lg="12"
              align-self="center"
            >
              <naj-input
                :type-id="formFieldType.checkbox"
                :value.sync="auditFields.isCaseTypeAudited"
                :label="`${local.requestReply.checklist.validateCaseType}`"
                class="inline-label primary-label-color"
                :disabled="isAuditFieldsDisabled"
                @change="updateAuditFields({ isCaseTypeAudited: auditFields.isCaseTypeAudited })"
              ></naj-input>
            </v-col>
          </v-col>

          <v-col
            v-if="showStepCaseCategoryDetailsPanel && caseData.caseTypeId != null"
            cols="12"
            lg="12"
            class="d-flex flex-column step-case-category-main-panel"
          >
            <naj-alert type="information">
              <template slot="content">
                <div class="d-flex justify-space-between align-center">
                  <div class="mt-1 text--secondary">
                    <step-case-category-details-panel
                      :notes="null"
                      :alert="alert"
                      :exceptions="exceptions"
                      :selected-case-type="selectedCaseType"
                      :acceptance-criteria="selectedCaseType.acceptanceCriteria"
                      :case-classification="selectedClassification.id"
                    ></step-case-category-details-panel>
                  </div>
                </div>
              </template>
            </naj-alert>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col
            cols="12"
            lg="4"
            class="step-case-category-right-panel"
          >
            <v-col class="search-wrapper" ref="searchWrap" tabindex="-1" @focusout="onWrapFocusOut">
              <naj-input
                :type-id="formFieldType.text"
                :label="local.common.lawsuitCategoriesSearch"
                :placeholder="local.common.searchPlaceholder"
                :loading="loadingSearchResult"
                :value.sync="searchKeyword"
                :disabled="mode.isViewMode ||auditFields.isCaseClassificationAudited||auditFields.isCaseCategoryAudited || auditFields.isCaseTypeAudited"
                @input="handleSearchInputChange"
              ></naj-input>
              <div
                v-if="searchResult.length > 0"
                class="search-list-wrapper"
              >
                <v-list>
                  <v-list-item
                    v-for="(item, index) in searchResult"
                    :key="index"
                    @click="handleSearchSelect(item)"
                  >
                    <v-list-item-title>{{ item.caseTypeName }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
              <div
                v-else-if="!loadingSearchResult && searchKeyword && searchKeyword.trim().length > 0"
                class="search-list-wrapper no-data"
              >
                <v-list>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ local.common.selectBoxNoData }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>

            <v-col
              cols="12"
              lg="12"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.lawsuitDetails.caseClassification"
                :value.sync="caseData.caseClassificationIdNew"
                :disabled="mode.isViewMode || auditFields.isCaseClassificationAudited"
                :items="userCaseClassifications"
                item-value="id"
                item-text="name"
                validates="required"
                @change="caseClassificationIdChanged"
              ></naj-input>

              <div class="step-case-category-sub-panel">
                <step-case-category-details-panel
                  v-if="selectedClassification && (selectedClassification.notes || selectedClassification.alert || selectedClassification.exceptions)"
                  :notes="selectedClassification.notes"
                  :alert="selectedClassification.alert"
                  :exceptions="selectedClassification.exceptions"
                  :case-classification="selectedClassification.id"
                ></step-case-category-details-panel>
              </div>
            </v-col>
            <v-col
              v-if="isEditMode && !isInternalLawsuit && isNotCompleted"
              cols="12"
              lg="12"
              align-self="center"
            >
              <naj-input
                :type-id="formFieldType.checkbox"
                :value.sync="auditFields.isCaseClassificationAudited"
                :label="`${local.requestReply.checklist.validateCaseClassification}`"
                class="inline-label primary-label-color"
                :disabled="isAuditFieldsDisabled"
                @change="updateAuditFields({ isCaseClassificationAudited: auditFields.isCaseClassificationAudited })"
              ></naj-input>
            </v-col>
            <v-col
              cols="12"
              lg="12"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.lawsuitDetails.caseCategory"
                :value.sync="caseData.caseCategoryId"
                :disabled="mode.isViewMode || auditFields.isCaseCategoryAudited"
                :items="userCaseCategories[caseData.caseClassificationIdNew]"
                item-value="id"
                item-text="name"
                validates="required"
                @change="caseCategoryIdChanged(true)"
              ></naj-input>

              <div class="step-case-category-sub-panel">
                <step-case-category-details-panel
                  v-if="selectedCaseCategory && (selectedCaseCategory.notes || selectedCaseCategory.alert|| selectedCaseCategory.exceptions)"
                  :notes="selectedCaseCategory.notes"
                  :alert="selectedCaseCategory.alert"
                  :exceptions="selectedCaseCategory.exceptions"
                  :case-classification="selectedClassification.id"
                ></step-case-category-details-panel>
              </div>
            </v-col>
            <v-col
              v-if="isEditMode && !isInternalLawsuit && isNotCompleted"
              cols="12"
              lg="12"
              align-self="center"
            >
              <naj-input
                :type-id="formFieldType.checkbox"
                :value.sync="auditFields.isCaseCategoryAudited"
                :label="`${local.requestReply.checklist.validateCaseCategory}`"
                class="inline-label primary-label-color"
                :disabled="isAuditFieldsDisabled"
                @change="updateAuditFields({ isCaseCategoryAudited: auditFields.isCaseCategoryAudited })"
              ></naj-input>
            </v-col>

            <v-col
              cols="12"
              lg="12"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.lawsuitDetails.caseTypeId"
                :value.sync="caseData.caseTypeId"
                :disabled="mode.isViewMode || auditFields.isCaseTypeAudited"
                :items="userCaseTypes[caseData.caseCategoryId]"
                item-value="id"
                item-text="name"
                validates="required"
                @change="caseTypeChangeHandler(true)"
              ></naj-input>

              <div class="step-case-category-sub-panel">
                <step-case-category-details-panel
                  v-if="selectedCaseType && (selectedCaseType.notes || selectedCaseType.alert|| selectedCaseType.exceptions)"
                  :notes="selectedCaseType.notes"
                  :alert="selectedCaseType.alert"
                  :exceptions="selectedCaseType.exceptions"
                  :case-classification="selectedClassification.id"
                  :acceptance-criteria="selectedCaseType.acceptanceCriteria"
                ></step-case-category-details-panel>
              </div>
            </v-col>
            <v-col
              v-if="isEditMode && !isInternalLawsuit && isNotCompleted"
              cols="12"
              lg="12"
              align-self="center"
            >
              <naj-input
                :type-id="formFieldType.checkbox"
                :value.sync="auditFields.isCaseTypeAudited"
                :label="`${local.requestReply.checklist.validateCaseType}`"
                class="inline-label primary-label-color"
                :disabled="isAuditFieldsDisabled"
                @change="updateAuditFields({ isCaseTypeAudited: auditFields.isCaseTypeAudited })"
              ></naj-input>
            </v-col>
          </v-col>
          <v-col
            v-if="showStepCaseCategoryDetailsPanel"
            class="step-case-category-main-panel col col-lg-8 col-sm-12 col-12"
          >
            <step-case-category-details-panel
              :notes="notes"
              :alert="alert"
              :exceptions="exceptions"
              :selected-case-type="selectedCaseType"
              :acceptance-criteria="selectedCaseType.acceptanceCriteria"
              :case-classification="selectedClassification.id"
            ></step-case-category-details-panel>
          </v-col>
        </v-row>

        <v-row>
          <!-- <template v-if="showRelatedToCoronaControls">
            <v-col
              cols="8"
              lg="8"
            >
              <naj-input
                :type-id="formFieldType.checkbox"
                :value.sync="caseData.isRelatedToCorona"
                :label="local.lawsuitDetails.isRelatedToCorona"
                class="inline-label primary-label-color pledged"
                :disabled="mode.isViewMode"
                @change="isRelatedToCoronaChange"
              ></naj-input>
            </v-col>

            <v-col
              v-if="caseData.isRelatedToCorona"
              cols="8"
              lg="8"
            >
              <naj-input
                :type-id="formFieldType.textarea"
                :value.sync="caseData.relatedToCoronaReasons"
                :label="local.lawsuitDetails.relatedToCoronaReasons"
                :disabled="mode.isViewMode"
                validates="required|arabic_letters|max:250"
                rows="2"
                @input="relatedToCoronaReasonsInput"
              ></naj-input>
            </v-col>
          </template> -->

          <v-col
            v-if="isNewMode &&( (caseData.caseTypeId && caseData.isSmartCaseType) || !caseData.isSmartCaseType)"
            cols="8"
            lg="8"
          >
            <naj-input
              :type-id="formFieldType.checkbox"
              :value.sync="pledged"
              :label="local.lawsuitDetails.pledge.pledgeLabel"
              class="inline-label primary-label-color pledged"
              :disabled="!isNewMode "
              @change="pledgedChange"
            ></naj-input>
            <div
              v-if="submitted && !pledged"
              class="error--text"
            >
              {{ local.lawsuitDetails.pledge.pledgeErrorText }}
            </div>
          </v-col>
        </v-row>
      </template>
    </naj-box>

    <v-row v-if="caseData.isSmartCaseType != null">
      <v-col
        cols="12"
        class="text-left"
      >
        <naj-button
          v-if="!selectedCaseType.isRedirectToExternalService"
          type="submit"
        >
          {{ local.common.next }}
        </naj-button>
        <naj-button
          v-else
          @click="redirectExternalServiceUrl"
        >
          {{ local.common.redirectToExternalService }}
        </naj-button>
      </v-col>
    </v-row>
    <naj-confirmation
      :show-modal="showRemovePopup"
      color="error"
      :title="local.partyAttributes.removeConfirmation.title"
      :message="local.partyAttributes.removeConfirmation.message"
      :ok-btn-label="local.common.remove"
      @ok="removeConfirmed"
      @cancel="removeCanceled"
    ></naj-confirmation>
  </form>
</template>

<script>
/* eslint-disable no-param-reassign */
import debounce from 'debounce';
import { isLaborLawsuit } from '../../helpers/utils';
import { scrollIntoInValidInput } from '@/helpers/utils';
import { createNamespacedHelpers } from 'vuex';

const { mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');
const { mapActions, mapState, mapGetters } = createNamespacedHelpers('LawsuitModule');
const { mapState: mapLUTState, mapActions: mapLUTActions } = createNamespacedHelpers(
  'LookupsModule'
);
import StepCaseCategoryDetailsPanel from './step-case-category-details-panel';
import NajTree from '@/components/shared/naj-tree/index';
import { caseClassificationEnum, casePartyRoleEnum } from '@/helpers/enum';
import { computeIncompatibleParties } from '@/modules/lawsuit/helpers/party-attribute-compatibility';
import axios from 'axios';

export default {
  inject: ['mode', '$validator'],
  components: {
    StepCaseCategoryDetailsPanel,
    NajTree,
  },
  data() {
    return {
      isLaborLawsuit,
      caseClassificationEnum,
      casePartyRoleEnum,
      debounceTimeout: null,
      isCaseTypeAudited: false,
      notes: '',
      alert: '',
      exceptions: '',
      searchKeyword: '',
      searchResult: [],
      loadingSearchResult: false,
      submitted: false,
      showStepCaseCategoryDetailsPanel: false,
      caseData: {},
      loading: false,
      questions: [],
      rootItem: {},
      selectedQuestions: [],
      oldCaseTypeId: null,
      oldCaseClassificationId: null,
      oldCaseCategoryId: null,
      showRemovePopup: false,
      pendingDeleteKeys: [],
      debouncedSearch: null,
    };
  },
  watch: {
    caseTypeId: {
      deep: true,
      handler() {
        this.$validator.flag('caseTypeId', {
          untouched: false,
          touched: true,
          changed: true,
        });
      },
    },
    'caseData.caseClassificationIdNew'(value) {
      value && this.getUserCaseCategories(value).then(() => {
        this.$nextTick(this.caseCategoryIdChanged);
      });
    },
    'caseData.caseCategoryId'(value) {
      this.caseData.caseTypeId && value && this.getSelectedCaseTypeData();
      value && this.getUserCaseTypes(value);
      value && this.getCaseTypesByCategory(value).then(() => {
        this.$nextTick(this.caseTypeChangeHandler);
      });
    },
    lawsuitRequestCaseData: {
      immediate: true,
      handler({ caseClassificationIdNew, caseCategoryId, caseTypeId, isRelatedToCorona, relatedToCoronaReasons, caseHasChanges, isSmartCaseType, caseTypeQuestionList }) {
        this.caseData = {
          caseClassificationIdNew,
          caseCategoryId,
          caseTypeId,
          isRelatedToCorona,
          relatedToCoronaReasons,
          caseHasChanges,
          isSmartCaseType,
          caseTypeQuestionList,
        };
        if (this.oldCaseTypeId == null && caseTypeId) {
          this.oldCaseTypeId = caseTypeId;
        }
        if(this.oldCaseCategoryId == null && caseCategoryId){
          this.oldCaseCategoryId = caseCategoryId;
        }
        if (this.oldCaseClassificationId == null && caseClassificationIdNew) {
          this.oldCaseClassificationId = caseClassificationIdNew;
        }
      },
    },
  },
  created() {
    this.debouncedSearch = debounce(this.handleSearchCasesTypes, 1000);
    this.getUserCaseClassifications().then(() => {
      this.caseData.caseClassificationIdNew && this.getUserCaseCategories(this.caseData.caseClassificationIdNew).then(() => {
        this.$nextTick(this.caseCategoryIdChanged);
        this.caseData.caseCategoryId && this.getUserCaseTypes(this.caseData.caseCategoryId);
        this.caseData.caseCategoryId && this.getCaseTypesByCategory(this.caseData.caseCategoryId).then(() => {
          this.$nextTick(this.caseTypeChangeHandler);
          this.getSelectedCaseTypeData();
        });
      });
    });
    if (this.isNewMode) {
      this.loading = true;
      this.getCaseTypeTree()
        .then((result) => {
          if (result != null) {
            this.questions = JSON.parse(result[0].treeJson).questions;
            this.rootItem = this.questions.find(x => x.isRootQuestion == true);
            this.selectedQuestions = [];
            this.loading = false;
          }
        })
        .catch((err) => {
          this.loading = false;
          console.error('Failed to fetch JSON:', err);
        });
    }
  },
  mounted() {
    if (!this.isNewMode) {
      this.submitted = true;
    }
    if (this.caseData.isSmartCaseType == true) {
      this.$validator.attach({
        name: 'caseTypeId',
        getter: () => this.caseTypeId,
        rules: 'required',
      });
      this.$validator.localize({ ar: { attributes: { caseTypeId: this.local.lawsuitDetails.caseTypeId } } });
    }
  },
  beforeDestroy() {
    if (this.debouncedSearch) this.debouncedSearch.clear();
  },
  computed: {
    ...mapState({
      lawsuitRequestCaseData: state => state.lawsuitRequest.case,
      auditFields: state => state.lawsuitRequest.auditFields,
      pledged: state => state.isPledged,
      // searchResult: (state) => state.lawsuitRequest.caseTypesSearchResult,
    }),
    ...mapGetters([
      'isViewMode',
      'isNewMode',
      'isEditMode',
      'isNotCompleted',
      'isInternalLawsuit',
      'isAuditFieldsDisabled',
      'prosecutors',
      'defendants'
    ]),
    ...mapLUTState(['caseTypesByCategory', 'userCaseTypes', 'userCaseCategories', 'userCaseClassifications']),
    caseTypeId() {
      return this.caseData.caseTypeId;
    },
    selectedClassification() {
      return this.userCaseClassifications.find(i => i.id === this.caseData.caseClassificationIdNew) || {};
    },
    selectedCaseCategory() {
      const userCaseCategories = this.userCaseCategories[this.caseData.caseClassificationIdNew];
      return (userCaseCategories || []).find(c => c.id === this.caseData.caseCategoryId) || {};
    },
    selectedCaseType() {
      const caseTypes = this.caseTypesByCategory[this.caseData.caseCategoryId];
      return (caseTypes || []).find(c => c.id === this.caseData.caseTypeId) || {};
    },
    nextButtonLabel() {
      if (this.isViewMode || this.isNewMode || !this.caseData.caseHasChanges) {
        return this.local.common.next;
      }
      if (this.isEditMode) {
        return this.local.common.saveAndContinue;
      }
      return '';
    },
    showRelatedToCoronaControls() {
      return this.caseData.caseClassificationIdNew == this.caseClassificationEnum.general;
    },
  },
  methods: {
    onWrapFocusOut(e) {
      const wrap = this.$refs.searchWrap;
      if (!wrap) return;
      const next = e.relatedTarget;
      if (next && wrap.contains(next)) return; 
      this.searchKeyword = '';
      this.searchResult = [];
    },
    ...mapActions({
      save: 'stepCaseCategoryNext',
      addCaseTypeDynamicFormModel: 'addCaseTypeDynamicFormModel',
      updateAuditFields: 'updateAuditFields',
      resetCaseTypeDynamicFormModel: 'resetCaseTypeDynamicFormModel',
      searchCasesTypes: 'searchCasesTypes',
      updateIsPledged: 'updateIsPledged',
      resetCaseCourtId: 'resetCaseCourtId',
      getActivePartyAttributes: 'getActivePartyAttributes',
      deleteCasePartiesByObjectKeys: 'deleteCasePartiesByObjectKeys',
      resetCasePartiesByObjectKeys: 'resetCasePartiesByObjectKeys',
    }),
    ...mapGlobalActions(['getDynamicForm', 'getCaseType', 'getCaseCategory']),
    ...mapLUTActions(['getUserCaseClassifications', 'getUserCaseCategories', 'getUserCaseTypes', 'getCaseTypesByCategory', 'getCaseTypeTree']),
     removeCanceled() {
      // revert to previous caseType & stop navigation
      if (this.oldCaseClassificationId) this.$set(this.caseData, 'caseClassificationIdNew', this.oldCaseClassificationId);
      if (this.oldCaseCategoryId) this.$set(this.caseData, 'caseCategoryId', this.oldCaseCategoryId);
      if (this.oldCaseTypeId) this.$set(this.caseData, 'caseTypeId', this.oldCaseTypeId);
      this.pendingDeleteKeys = [];
      this.showRemovePopup = false;
       this.$nextTick(() => {
        this.save(this.caseData);
      });
    },
    async removeConfirmed() {
      if (this.isEditMode) {
        // Parties already in DB - delete from DB
        await this.deleteCasePartiesByObjectKeys(this.pendingDeleteKeys);
      } else {
        // Clear local parties from state store
        await this.resetCasePartiesByObjectKeys(this.pendingDeleteKeys);
      }
      this.pendingDeleteKeys = [];
      this.showRemovePopup = false;
      this.$nextTick(() => {
        this.save(this.caseData);
      });
        
    },
   async handleCaseTypeChangeBeforeNext() {
      const prev = this.oldCaseTypeId ?? null;
      const curr = this.selectedCaseType?.id ?? null;
      if (curr == prev) return true;
      const pros = Object.values(this.prosecutors || {});
      const defs = Object.values(this.defendants || {});
      if (pros.length == 0 && defs.length == 0) {
        this.oldCaseTypeId = curr;
        return true;
      }
     const newDefsAllowed = !this.selectedCaseType?.mustHaveNoDefendant;
      const [newAttrsPros, newAttrsDefs] = await Promise.all([
        this.getActivePartyAttributes({ caseTypeId: curr, casePartyRole: this.casePartyRoleEnum.prosecutor }),
        newDefsAllowed
          ? this.getActivePartyAttributes({ caseTypeId: curr, casePartyRole: this.casePartyRoleEnum.defendant })
          : Promise.resolve([]),
      ]);
      const { incompatiblePros, incompatibleDefs } = computeIncompatibleParties({
        pros, defs, newAttrsPros, newAttrsDefs, newDefsAllowed,
      });
      const totalIncompat = incompatiblePros.length + incompatibleDefs.length;
      if (totalIncompat > 0) {
        this.pendingDeleteKeys = [
          ...incompatiblePros.map(p => p.objectKey),
          ...incompatibleDefs.map(d => d.objectKey),
        ];
        this.showRemovePopup = true;
        return false; 
      }
      this.oldCaseTypeId = curr;
      return true;
    },
    submit() {
      if (this.isEditMode)
        this.callGetUserCaseTypes(this.caseData.caseCategoryId, this.selectedCaseType.id);
      this.$nextTick(() => {
        this.$validator.validateAll().then(async (isValid) => {
          this.submitted = true;
          if (isValid && this.pledged) {
            const ok = await this.handleCaseTypeChangeBeforeNext();
            if (!ok) return;
            this.save(this.caseData);
          } else {
            scrollIntoInValidInput(this.errors);
          }
        });
      });
    },
    callGetUserCaseTypes(categoryId, caseTypeId) {
      return this.getUserCaseTypes(categoryId)
        .then((response) => {
          if (caseTypeId) {
            if (!(response || []).some(ct => ct.id == caseTypeId))
              this.$set(this.caseData, 'caseTypeId', undefined);
            else
              this.$set(this.caseData, 'caseTypeId', caseTypeId);
          }
          return response;
        });
    },
    redirectExternalServiceUrl() {
      window.location.href = this.selectedCaseType.redirectExternalServiceUrl;
    },
    caseClassificationIdChanged(value) {
      const selectedClassification = this.userCaseClassifications.find(i => i.id === value);
      this.hideStepCaseCategoryDetailsPanel();

      if (
        (
          selectedClassification
        ) &&
        (
          selectedClassification.notes ||
          selectedClassification.alert ||
          selectedClassification.exceptions
        )
      ) {
        this.notes = selectedClassification.notes;
        this.alert = selectedClassification.alert;
        this.exceptions = selectedClassification.exceptions;
        this.showStepCaseCategoryDetailsPanel = true;
      }

      this.$set(this.caseData, 'caseCategoryId', null);
      this.$set(this.caseData, 'caseTypeId', null);
      this.getUserCaseCategories(value);
      this.$set(this.caseData, 'caseHasChanges', true);

      if (!this.showRelatedToCoronaControls) {
        this.$set(this.caseData, 'isRelatedToCorona', null);
        this.$set(this.caseData, 'relatedToCoronaReasons', null);
      }
    },
    hideStepCaseCategoryDetailsPanel() {
      this.showStepCaseCategoryDetailsPanel = false;
      this.note = '';
      this.alert = '';
      this.exceptions = '';
    },
    caseCategoryIdChanged(isActualChange) {
      if (isActualChange) {
        this.hideStepCaseCategoryDetailsPanel();

        if (
          this.selectedCaseCategory.notes ||
          this.selectedCaseCategory.alert ||
          this.selectedCaseCategory.exceptions
        ) {
          this.notes = this.selectedCaseCategory.notes;
          this.alert = this.selectedCaseCategory.alert;
          this.exceptions = this.selectedCaseCategory.exceptions;
          this.showStepCaseCategoryDetailsPanel = true;
        }

        this.$set(this.caseData, 'caseTypeId', null);
        this.$nextTick(() => {
          this.getUserCaseTypes(this.caseData.caseCategoryId);
          this.getCaseTypesByCategory(this.caseData.caseCategoryId);
        });
        this.$set(this.caseData, 'caseHasChanges', true);
      }
    },
    caseTypeChangeHandler(isActualChange, caseType = null) {
      if (isActualChange) {
        this.hideStepCaseCategoryDetailsPanel();

        if (caseType != null && (
          caseType.notes ||
          caseType.alert ||
          caseType.exceptions
        )) {
          this.notes = caseType.notes;
          this.alert = caseType.alert;
          this.exceptions = caseType.exceptions;
          this.showStepCaseCategoryDetailsPanel = true;

        }
        else if (
          this.selectedCaseType.notes ||
          this.selectedCaseType.alert ||
          this.selectedCaseType.exceptions
        ) {
          this.notes = this.selectedCaseType.notes;
          this.alert = this.selectedCaseType.alert;
          this.exceptions = this.selectedCaseType.exceptions;
          this.showStepCaseCategoryDetailsPanel = true;
        }
        this.resetCaseTypeDynamicFormModel();
        this.$set(this.caseData, 'caseHasChanges', true);
      }
    },
    getNeededDynamicForm(formId) {
      this.getDynamicForm({
        formId,
        withoutParse: true,
      }).then((form) => {
        this.updateLawsuitDynamicForms(form);
        // eslint-disable-next-line eqeqeq
        if (!this.lawsuitRequestCaseData.forms.find(f => f.formName == form.name)) {
          this.addCaseTypeDynamicFormModel({
            formId,
            formName: form.name,
            caseTypeId: this.caseData.caseTypeId,
          });
        }
      });
    },
    handleSearchInputChange(value) {
      // return; // temp to fix performance issue
      if (!value || value.length < 3) {
        this.searchResult = [];
        this.debouncedSearch.clear();
        return;
      }
      this.debouncedSearch(value);
    },
    handleSearchCasesTypes(value) {
      if (value) {

        if (!this.searchKeyword || this.searchKeyword.length < 3) {
          this.searchResult = [];
          return;
        }

        this.loadingSearchResult = true;
        this.searchCasesTypes(value)
          .then((res) => {
            this.searchResult = res.data;
            this.loadingSearchResult = false;
          })
          .catch((err) => {
            if (!axios.isCancel(err)) {
              this.loadingSearchResult = false;
              this.searchResult = [];
            }
          })
          .finally(() => {
            this.loadingSearchResult = false;
          });
      }
    },
    handleSearchSelect(value) {
      this.searchResult = [];
      this.searchKeyword = '';
      this.$set(this.caseData, 'caseClassificationIdNew', value.caseClassificationId);
      this.$set(this.caseData, 'caseCategoryId', value.caseCategoryId);
      this.$nextTick(() => {
        this.callGetUserCaseTypes((value || {}).caseCategoryId, (value || {}).caseTypeId);
      });
      if (!this.showRelatedToCoronaControls) {
        this.$set(this.caseData, 'isRelatedToCorona', null);
        this.$set(this.caseData, 'relatedToCoronaReasons', null);
      }
      this.getSelectedCaseTypeData();
      this.caseTypeChangeHandler(true);
      this.showStepCaseCategoryDetailsPanel = true;
    },
    pledgedChange(value) {
      this.submitted = false;
      this.updateIsPledged(value);
    },
    isRelatedToCoronaChange(val) {
      this.$set(this.caseData, 'caseHasChanges', true);
      !val && this.$set(this.caseData, 'relatedToCoronaReasons', null);
    },
    relatedToCoronaReasonsInput() {
      this.$set(this.caseData, 'caseHasChanges', true);
    },
    getSelectedCaseTypeData() {
      if (this.caseData.caseCategoryId && (this.caseTypesByCategory[this.caseData.caseCategoryId] || []).length != 0) {
        this.hideStepCaseCategoryDetailsPanel();
        this.notes = this.selectedCaseType.notes;
        this.alert = this.selectedCaseType.alert;
        this.exceptions = this.selectedCaseType.exceptions;
        this.showStepCaseCategoryDetailsPanel = true;
        return;
      }
      this.caseData.caseCategoryId && this.getUserCaseTypes(this.caseData.caseCategoryId);
      this.caseData.caseCategoryId && this.getCaseTypesByCategory(this.caseData.caseCategoryId).then(() => {
        this.hideStepCaseCategoryDetailsPanel();
        this.notes = this.selectedCaseType.notes;
        this.alert = this.selectedCaseType.alert;
        this.exceptions = this.selectedCaseType.exceptions;
        this.showStepCaseCategoryDetailsPanel = true;
      });
    },
    switchRequest() {
      if (this.isNewMode) {
        this.$set(this.caseData, 'caseClassificationIdNew', null);
        this.$set(this.caseData, 'caseCategoryId', null);
        this.$set(this.caseData, 'caseTypeId', null);
        this.$set(this.caseData, 'isRelatedToCorona', null);
        this.$set(this.caseData, 'relatedToCoronaReasons', null);
        this.$set(this.caseData, 'caseTypeQuestionList', []);
      }
      this.showStepCaseCategoryDetailsPanel = false;
      this.$set(this.caseData, 'isSmartCaseType', !this.caseData.isSmartCaseType);
      if (this.caseData.isSmartCaseType == false) {
        this.$validator.detach('caseTypeId');
      } else {
        this.$validator.attach({
          name: 'caseTypeId',
          getter: () => this.caseTypeId,
          rules: 'required',
        });
      }
    },
    onSelect(val) {
      this.$set(this.caseData, 'isSmartCaseType', val);
      if (this.caseData.isSmartCaseType == false) {
        this.$validator.detach('caseTypeId');
      } else {
        this.$validator.attach({
          name: 'caseTypeId',
          getter: () => this.caseTypeId,
          rules: 'required',
          alias: this.local.lawsuitDetails.caseTypeId,

        });
      }
    },
    handleAnswer(answerData) {
      this.errors.remove('caseTypeId');
      this.$set(this.caseData, 'caseTypeId', null);
      const index = this.selectedQuestions.findIndex(
        x => x.questionId === answerData.res.questionId
      );
      if (index >= 0) {
        this.selectedQuestions[index] = answerData.res;
        this.selectedQuestions = this.selectedQuestions.slice(0, index + 1);
      } else {
        this.selectedQuestions.push(answerData.res);
      }
      //this.$set(this.caseData, 'caseTypeQuestionList', this.selectedQuestions);
      // if (answerData.isActualChange) {
      //   this.errors.add({
      //     field: 'caseTypeId',
      //     msg: this.local.caseTypeTree.caseTypeReq,
      //   });
      // }
      this.$set(this.caseData, 'caseHasChanges', true);
    },
    async handleFinalRes(data) {
      this.loading = true;
      this.errors.remove('caseTypeId');
      var questionsAndAnswers = [];
      for (let i = 0; i < this.selectedQuestions.length; i++) {
        questionsAndAnswers.push({
          questionId: this.selectedQuestions[i].questionId,
          questionName: this.selectedQuestions[i].questionText,
          selectedAnswerId: this.selectedQuestions[i].selectedAnswer.id,
          selectedAnswerName: this.selectedQuestions[i].selectedAnswer.text,
        });
      }
      this.$set(this.caseData, 'caseTypeQuestionList', questionsAndAnswers);
      if (this.caseData.caseTypeId != data.res) {
        const caseTypeResponse = await this.getCaseType(data.res);
        var userCaseTypes = await this.getUserCaseTypes(caseTypeResponse.caseCategoryId);
        const caseCategoryResponse = await this.getCaseCategory(caseTypeResponse.caseCategoryId);
        var isValid = (userCaseTypes || []).some(ct => ct.id == data.res);
        if (!isValid) {
          this.errors.add({
            field: 'caseTypeId',
            msg: this.local.caseTypeTree.caseTypeErr,
          });
          this.$set(this.caseData, 'caseTypeId', undefined);
        } else {
          this.$set(this.caseData, 'caseTypeId', data.res * 1);
          this.$set(this.caseData, 'caseClassificationIdNew', caseCategoryResponse.caseClassificationId);
          this.$set(this.caseData, 'caseCategoryId', caseTypeResponse.caseCategoryId);
          this.caseTypeChangeHandler(data.isActualChange, caseTypeResponse);
        }
      }
      this.loading = false;

    },
  },
};
</script>
<style lang="scss" scoped>
.step-case-category-right-panel {
  border-left: 2px solid #75d6ce;
  padding: 0px;
}

.error--text {
  font-size: 12px;
}

.pledged {
  ::v-deep .v-input__slot {
    align-items: flex-start;
  }
}

.search-wrapper {
  position: relative;

  .search-list-wrapper {
    position: absolute;
    left: 12px;
    right: 12px;
    top: 90px;
    height: 132px;
    overflow: auto;
    background-color: #fff;
    z-index: 10;
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
    box-sizing: content-box;
  }

  .no-data {
    height: auto;
  }
}

// Small devices (landscape phones, 576px and up)
@media (max-width: 576px) {
  .step-case-category-main-panel {
    display: none;
  }
  .step-case-category-sub-panel {
    display: block;
  }
  .step-case-category-right-panel {
    border: none !important;
  }
}
// Medium devices (tablets, 768px and up)
@media (min-width: 768px) {
  .step-case-category-main-panel {
    display: none;
  }
  .step-case-category-sub-panel {
    display: block;
  }
  .step-case-category-right-panel {
    border: none !important;
  }
}
// Large devices (desktops, 992px and up)
@media (min-width: 992px) {
  .step-case-category-main-panel {
    display: block;
  }
  .step-case-category-sub-panel {
    display: none;
  }
  .step-case-category-right-panel {
    border-left: 2px solid #75d6ce !important;
  }
}
// Extra large devices (large desktops, 1200px and up)
@media (min-width: 1200px) {
  .step-case-category-main-panel {
    display: block;
  }
  .step-case-category-sub-panel {
    display: none;
  }
  .step-case-category-right-panel {
    border-left: 2px solid #75d6ce !important;
  }
}
</style>
